import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

/** Mirrors `WorkflowVariable.KeyPattern` on the server. */
export const VARIABLE_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/;

/** Mirrors `WorkflowVariable.MaxVariables`. */
export const VARIABLE_MAX_COUNT = 50;

/** Mirrors `WorkflowVariable.MaxValueLength`. */
export const VARIABLE_MAX_VALUE_LENGTH = 500;

type VariableRow = { key?: string; value?: string };

/**
 * Validator for the whole list, registered on the `Form.List` itself.
 *
 * Reads the rows from the validator's own argument rather than from a captured prop: a
 * `Form.List`'s `rules` array is registered once on mount, so anything closed over would be
 * the value as it was then. Same trap as the options editor in `FieldPropertiesPanel`.
 *
 * Only ENTIRELY blank rows are skipped, matching `WorkflowVariable.NormalizeAndValidate` — a
 * row with a name and no value is legitimate (an empty string is a value), so it must still be
 * name-checked rather than quietly dropped.
 */
function variablesValidator(
  badName: (name: string) => string,
  duplicate: (name: string) => string,
  tooMany: string,
) {
  return (_: unknown, rows?: VariableRow[]) => {
    const list = rows ?? [];
    const seen = new Set<string>();
    for (const row of list) {
      const key = (row?.key ?? '').trim();
      const value = (row?.value ?? '').trim();
      if (key.length === 0 && value.length === 0) continue;
      if (!VARIABLE_KEY_PATTERN.test(key)) return Promise.reject(new Error(badName(key)));
      // Case-insensitive, like the server's uniqueness rule — `fund` and `Fund` would
      // otherwise both save and then resolve unpredictably.
      const lowered = key.toLowerCase();
      if (seen.has(lowered)) return Promise.reject(new Error(duplicate(key)));
      seen.add(lowered);
    }
    if (list.length > VARIABLE_MAX_COUNT) return Promise.reject(new Error(tooMany));
    return Promise.resolve();
  };
}

/**
 * The workflow's variables as an editable key/value list — the "environment variables" of a
 * workflow. A field prefills from one by selecting `variable.<name>` as its prefill source in
 * the form builder.
 *
 * Bound to the surrounding AntD form under the `variables` name, so the Advanced modal needs no
 * state of its own and the whole list rides the existing whole-state save.
 */
export function WorkflowVariablesEditor() {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Text strong style={{ fontSize: 12 }}>
        {t('workflow.form.variables.title')}
      </Text>
      <Text type="secondary" style={{ fontSize: 11 }}>
        {t('workflow.form.variables.hint')}
      </Text>
      <Form.List
        name="variables"
        rules={[
          {
            validator: variablesValidator(
              (name) => t('workflow.form.variables.badName', { name }),
              (name) => t('workflow.form.variables.duplicate', { name }),
              t('workflow.form.variables.tooMany', { max: VARIABLE_MAX_COUNT }),
            ),
          },
        ]}
      >
        {(rows, { add, remove }, { errors }) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.length === 0 && (
              <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>
                {t('workflow.form.variables.empty')}
              </Text>
            )}
            {rows.map((row) => (
              <div key={row.key} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <Form.Item name={[row.name, 'key']} style={{ flex: 1, marginBottom: 0 }}>
                  <Input
                    maxLength={64}
                    placeholder={t('workflow.form.variables.namePlaceholder')}
                    aria-label={t('workflow.form.variables.name')}
                  />
                </Form.Item>
                <Form.Item name={[row.name, 'value']} style={{ flex: 1.4, marginBottom: 0 }}>
                  <Input
                    maxLength={VARIABLE_MAX_VALUE_LENGTH}
                    placeholder={t('workflow.form.variables.valuePlaceholder')}
                    aria-label={t('workflow.form.variables.value')}
                  />
                </Form.Item>
                <Button
                  icon={<DeleteOutlined />}
                  aria-label={t('workflow.form.variables.remove', { name: row.name + 1 })}
                  onClick={() => remove(row.name)}
                />
              </div>
            ))}
            <Button
              size="small"
              icon={<PlusOutlined />}
              style={{ alignSelf: 'flex-start' }}
              onClick={() => add({ key: '', value: '' })}
            >
              {t('workflow.form.variables.add')}
            </Button>
            <Form.ErrorList errors={errors} />
          </div>
        )}
      </Form.List>
    </div>
  );
}
